/**
 * Magazzino Operatore — pagina dedicata per il magazziniere su PC fisso.
 * - Lock screen con PIN (default 1234, modificabile)
 * - 5 moduli: Inventario, Carico, Scarico, Ordini, DDT
 * - No sidebar admin: UI grande, click rapidi, friendly per PC fisso
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { BrowserMultiFormatReader } from "@zxing/browser";
import {
  Package, ArrowDownCircle, ArrowUpCircle, ScanLine, ClipboardList, Truck,
  LogOut, Lock, KeyRound, Search, AlertTriangle, Plus, Trash2, FileText,
  CheckCircle2, Circle, X, ChevronLeft, Settings, Download, Camera,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SESSION_KEY = "magazzino_unlocked";

// Fetch helper sicuro: legge il body UNA volta come testo + parse manuale.
// Evita "body stream already read" e doppi await.
const wfJson = async (path, opts = {}) => {
  try {
    const r = await fetch(`${API}${path}`, {
      ...opts,
      headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    });
    const txt = await r.text();
    let data = null;
    if (txt) {
      try { data = JSON.parse(txt); } catch { data = { _raw: txt }; }
    }
    return { ok: r.ok, status: r.status, data: data || {} };
  } catch (e) {
    return { ok: false, status: 0, data: { detail: e.message || "Errore di rete" } };
  }
};

// ===================== ROOT =====================
export default function MagazzinoOperatorePage() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1");
  const [view, setView] = useState("home");

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setUnlocked(false);
    setView("home");
  };

  if (!unlocked) {
    return <PinLockScreen onUnlock={() => { sessionStorage.setItem(SESSION_KEY, "1"); setUnlocked(true); }} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fdf6f3] via-white to-[#fef3e8]">
      {/* Top bar */}
      <div className="bg-white border-b border-rose-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => setView("home")} className="flex items-center gap-3" data-testid="magazzino-home-btn">
            <div className="bg-gradient-to-br from-[#B87A8A] to-[#A06978] text-white rounded-xl p-2">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-bold text-[#5A3A3A] text-lg leading-tight">Magazzino ODR</h1>
              <p className="text-xs text-gray-500">Postazione operatore</p>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setView("settings")}
              className="text-sm text-gray-600 hover:text-[#5A3A3A] px-3 py-1.5 rounded-lg hover:bg-gray-50 flex items-center gap-1.5"
              data-testid="magazzino-settings-btn"
            >
              <Settings className="w-4 h-4" /> Impostazioni
            </button>
            <button
              onClick={logout}
              className="text-sm text-gray-600 hover:text-rose-600 px-3 py-1.5 rounded-lg hover:bg-rose-50 flex items-center gap-1.5"
              data-testid="magazzino-logout"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {view !== "home" && (
          <button onClick={() => setView("home")} className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-[#5A3A3A]" data-testid="magazzino-back">
            <ChevronLeft className="w-4 h-4" /> Torna alla home
          </button>
        )}

        {view === "home" && <HomeTiles onSelect={setView} />}
        {view === "inventory" && <InventoryView />}
        {view === "load" && <MovementView mode="in" />}
        {view === "unload" && <MovementView mode="out" />}
        {view === "orders" && <OrdersView />}
        {view === "ddt" && <DdtView />}
        {view === "settings" && <SettingsView />}
      </div>
    </div>
  );
}

// ===================== PIN LOCK =====================
function PinLockScreen({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (value) => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const { ok } = await wfJson("/warehouse/pin/verify", { method: "POST", body: JSON.stringify({ pin: value }) });
      if (ok) onUnlock();
      else {
        setErr("PIN errato");
        setPin("");
      }
    } catch {
      setErr("Errore di rete");
    }
    setBusy(false);
  };

  const press = (n) => {
    if (pin.length >= 8) return;
    const next = pin + n;
    setPin(next);
    setErr("");
  };
  const back = () => setPin(pin.slice(0, -1));
  const confirm = () => pin.length >= 4 && submit(pin);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#5A3A3A] via-[#7a4a52] to-[#A06978] flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full" data-testid="pin-lock-screen">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center bg-gradient-to-br from-[#B87A8A] to-[#A06978] rounded-2xl p-4 mb-3">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#5A3A3A]">Accesso Magazzino</h1>
          <p className="text-sm text-gray-500 mt-1">Inserisci il PIN per continuare</p>
        </div>

        {/* PIN display */}
        <div className="flex justify-center gap-3 mb-6" data-testid="pin-dots">
          {[0, 1, 2, 3, 4, 5, 6, 7].slice(0, Math.max(4, pin.length)).map((i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full transition ${
                i < pin.length ? "bg-[#B87A8A]" : "bg-gray-200"
              }`}
            />
          ))}
        </div>
        {err && (
          <p className="text-center text-rose-600 text-sm mb-3 animate-pulse" data-testid="pin-error">{err}</p>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              onClick={() => press(String(n))}
              className="h-16 rounded-2xl bg-gray-50 hover:bg-rose-50 active:scale-95 transition text-2xl font-semibold text-[#5A3A3A]"
              data-testid={`pin-key-${n}`}
            >
              {n}
            </button>
          ))}
          <button
            onClick={back}
            className="h-16 rounded-2xl bg-gray-50 hover:bg-gray-100 active:scale-95 transition text-[#5A3A3A]"
            data-testid="pin-back"
          >
            <ChevronLeft className="w-6 h-6 mx-auto" />
          </button>
          <button
            onClick={() => press("0")}
            className="h-16 rounded-2xl bg-gray-50 hover:bg-rose-50 active:scale-95 transition text-2xl font-semibold text-[#5A3A3A]"
            data-testid="pin-key-0"
          >
            0
          </button>
          <button
            onClick={confirm}
            disabled={busy || pin.length < 4}
            className="h-16 rounded-2xl bg-gradient-to-br from-[#B87A8A] to-[#A06978] text-white disabled:opacity-40 active:scale-95 transition font-semibold"
            data-testid="pin-confirm"
          >
            OK
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          PIN di default: <span className="font-mono">1234</span> (modificabile da Impostazioni)
        </p>
      </div>
    </div>
  );
}

// ===================== HOME TILES =====================
function HomeTiles({ onSelect }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    wfJson("/warehouse/stats").then(({ data }) => setStats(data));
  }, []);

  const tiles = [
    {
      key: "inventory",
      label: "Inventario",
      desc: "Vedi prodotti, scorte e scadenze",
      Icon: ClipboardList,
      grad: "from-violet-500 to-purple-600",
      badge: stats ? `${stats.products} prodotti` : "",
    },
    {
      key: "load",
      label: "Carico",
      desc: "Entrata merce / rifornimento",
      Icon: ArrowDownCircle,
      grad: "from-emerald-500 to-green-600",
    },
    {
      key: "unload",
      label: "Scarico",
      desc: "Uscita merce / consumi interni",
      Icon: ArrowUpCircle,
      grad: "from-orange-500 to-rose-500",
    },
    {
      key: "orders",
      label: "Evasione ordini",
      desc: "Prepara e spedisci ordini",
      Icon: Package,
      grad: "from-sky-500 to-blue-600",
    },
    {
      key: "ddt",
      label: "DDT corriere",
      desc: "Documenti di trasporto",
      Icon: Truck,
      grad: "from-amber-500 to-orange-600",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#5A3A3A]">Buongiorno 👋</h2>
        <p className="text-gray-500">Cosa vuoi fare oggi?</p>
      </div>

      {stats && stats.low_stock_count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3" data-testid="home-alert">
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">{stats.low_stock_count} prodotti sotto soglia minima</p>
            <p className="text-xs text-amber-700">Vai in Inventario per vederli e fare un carico.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((t) => (
          <button
            key={t.key}
            onClick={() => onSelect(t.key)}
            className={`bg-gradient-to-br ${t.grad} text-white rounded-3xl p-6 text-left shadow-lg hover:shadow-2xl hover:-translate-y-1 active:scale-95 transition-all min-h-[180px] flex flex-col justify-between group`}
            data-testid={`tile-${t.key}`}
          >
            <div className="flex items-start justify-between">
              <t.Icon className="w-12 h-12 opacity-90 group-hover:scale-110 transition" strokeWidth={1.5} />
              {t.badge && <span className="bg-white/20 text-xs font-semibold px-2.5 py-1 rounded-full">{t.badge}</span>}
            </div>
            <div>
              <h3 className="text-xl font-bold">{t.label}</h3>
              <p className="text-sm opacity-90 mt-1">{t.desc}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ===================== INVENTORY =====================
function InventoryView() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [lowOnly, setLowOnly] = useState(false);

  useEffect(() => {
    const qs = lowOnly ? "?low_stock_only=true" : "";
    wfJson(`/warehouse/products${qs}`).then(({ data }) => setItems(data.items || []));
  }, [lowOnly]);

  const filtered = items.filter(
    (p) =>
      !filter ||
      p.name?.toLowerCase().includes(filter.toLowerCase()) ||
      p.sku?.toLowerCase().includes(filter.toLowerCase()) ||
      (p.ean || "").includes(filter)
  );

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-[#5A3A3A] flex items-center gap-2">
        <ClipboardList className="w-7 h-7" /> Inventario
      </h2>
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Cerca nome, SKU, EAN..."
            className="pl-10 h-12 text-base"
            data-testid="inventory-search"
          />
        </div>
        <button
          onClick={() => setLowOnly(!lowOnly)}
          className={`h-12 px-5 rounded-xl font-medium border-2 transition ${
            lowOnly ? "bg-amber-100 border-amber-400 text-amber-800" : "bg-white border-gray-200 text-gray-600 hover:border-amber-300"
          }`}
          data-testid="inventory-low-toggle"
        >
          <AlertTriangle className="w-4 h-4 inline mr-1" /> Solo scorte basse
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden" data-testid="inventory-table">
        <table className="w-full">
          <thead className="bg-[#fdf6f3]">
            <tr className="text-left text-xs uppercase text-[#5A3A3A]">
              <th className="px-4 py-3">Prodotto</th>
              <th className="px-4 py-3">SKU / EAN</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Min</th>
              <th className="px-4 py-3">Ubicazione</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rose-50">
            {filtered.map((p) => {
              const low = p.stock <= p.min_stock;
              return (
                <tr key={p.product_id} className={low ? "bg-amber-50/50" : ""} data-testid={`inv-row-${p.sku}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#5A3A3A]">{p.name}</p>
                    {p.ml && <p className="text-xs text-gray-500">{p.ml} ml</p>}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-600">
                    {p.sku}
                    {p.ean && <p className="text-gray-400">{p.ean}</p>}
                  </td>
                  <td className={`px-4 py-3 text-right text-2xl font-bold ${low ? "text-amber-600" : "text-emerald-600"}`}>
                    {p.stock}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400">{p.min_stock}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{p.location || "—"}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-gray-400 py-8">Nessun prodotto</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== CARICO / SCARICO =====================
function MovementView({ mode }) {
  const [code, setCode] = useState("");
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [lot, setLot] = useState("");
  const [recent, setRecent] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const inputRef = useRef(null);

  const isIn = mode === "in";
  const colorMain = isIn ? "emerald" : "rose";

  const loadRecent = useCallback(() => {
    wfJson(`/warehouse/movements?limit=10`).then(({ data }) => {
      setRecent((data.items || []).filter((m) => m.type === mode).slice(0, 8));
    });
  }, [mode]);

  useEffect(() => {
    loadRecent();
    inputRef.current?.focus();
  }, [loadRecent]);

  const scan = async (codeArg) => {
    const c = (codeArg !== undefined ? codeArg : code).trim();
    if (!c) return;
    setCode(c);
    const { ok, data } = await wfJson(`/warehouse/products/scan/${encodeURIComponent(c)}`);
    if (ok) {
      setProduct(data);
      setNotFound(false);
    } else {
      setProduct(null);
      if (isIn) {
        // in CARICO, se il codice non esiste, propongo di creare il nuovo prodotto
        setNotFound(true);
      } else {
        toast.error(`Codice "${c}" non trovato in anagrafica`);
        setNotFound(false);
      }
    }
  };

  const submit = async () => {
    if (!product) return toast.error("Scansiona prima un prodotto");
    if (submitting) return;
    setSubmitting(true);
    const { ok, data } = await wfJson("/warehouse/movements", {
      method: "POST",
      body: JSON.stringify({
        type: mode,
        product_id: product.product_id,
        quantity: parseInt(qty),
        notes: notes + (lot ? ` [Lotto ${lot}]` : ""),
        operator: "magazzino",
      }),
    });
    if (ok && data.success) {
      toast.success(`${isIn ? "Carico" : "Scarico"} registrato. Nuovo stock: ${data.new_stock}`);
      if (data.low_stock) toast.warning(`⚠ Scorta sotto soglia (${data.new_stock})`);
      setCode("");
      setProduct(null);
      setNotFound(false);
      setQty(1);
      setNotes("");
      setLot("");
      loadRecent();
      inputRef.current?.focus();
    } else toast.error(data.detail || "Errore");
    setSubmitting(false);
  };

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-[#5A3A3A] flex items-center gap-2">
        {isIn ? <ArrowDownCircle className="w-7 h-7 text-emerald-500" /> : <ArrowUpCircle className="w-7 h-7 text-rose-500" />}
        {isIn ? "Carico merce (Entrata)" : "Scarico merce (Uscita)"}
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-rose-100 p-6 space-y-4 shadow-sm">
          <div className="flex items-center gap-2 text-[#5A3A3A]">
            <ScanLine className="w-5 h-5" />
            <h3 className="font-semibold">1. Scansiona codice o digita SKU/EAN</h3>
          </div>
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && scan()}
              placeholder="Spara con la pistola o digita..."
              className="h-14 text-xl font-mono"
              data-testid="mov-code-input"
            />
            <Button onClick={() => scan()} className={`h-14 px-6 bg-${colorMain}-600 hover:bg-${colorMain}-700 text-base`} data-testid="mov-scan-btn">
              Cerca
            </Button>
            <Button
              onClick={() => setCameraOpen(true)}
              variant="outline"
              className="h-14 px-4 border-2"
              title="Scansiona con fotocamera (cellulare o webcam)"
              data-testid="mov-camera-btn"
            >
              <Camera className="w-5 h-5" />
            </Button>
          </div>

          {cameraOpen && (
            <CameraScanner
              onCode={(c) => { setCameraOpen(false); scan(c); }}
              onClose={() => setCameraOpen(false)}
            />
          )}

          {/* Prodotto NON trovato in modalità Carico → form rapido creazione */}
          {isIn && notFound && !product && (
            <QuickCreateProduct
              ean={code}
              onCancel={() => { setNotFound(false); setCode(""); inputRef.current?.focus(); }}
              onCreated={(p) => {
                setProduct(p);
                setNotFound(false);
                toast.success("Prodotto creato — ora inserisci quantità");
              }}
            />
          )}

          {product && (
            <div className="border-t pt-4 space-y-4" data-testid="mov-product-found">
              <div className={`bg-${colorMain}-50 border border-${colorMain}-200 rounded-2xl p-4`}>
                <p className="text-xs uppercase text-gray-500 font-semibold">2. Prodotto trovato</p>
                <p className="font-bold text-lg text-[#5A3A3A] mt-1">{product.name}</p>
                <p className="text-sm text-gray-500 font-mono">{product.sku} · Stock attuale: <b className="text-[#5A3A3A]">{product.stock}</b></p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-gray-500 font-semibold">3. Quantità</label>
                  <div className="flex items-center gap-2 mt-1">
                    <button onClick={() => setQty(Math.max(1, parseInt(qty) - 1))} className="w-12 h-14 bg-gray-100 hover:bg-gray-200 rounded-xl text-2xl font-bold" data-testid="qty-minus">−</button>
                    <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="h-14 text-2xl text-center font-bold" data-testid="mov-qty" />
                    <button onClick={() => setQty(parseInt(qty) + 1)} className="w-12 h-14 bg-gray-100 hover:bg-gray-200 rounded-xl text-2xl font-bold" data-testid="qty-plus">+</button>
                  </div>
                </div>
                {isIn && (
                  <div>
                    <label className="text-xs uppercase text-gray-500 font-semibold">Lotto (opzionale)</label>
                    <Input value={lot} onChange={(e) => setLot(e.target.value)} className="h-14 mt-1" placeholder="es. L24-08" />
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs uppercase text-gray-500 font-semibold">Note</label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-12 mt-1" placeholder="Causale, destinazione, fornitore..." data-testid="mov-notes" />
              </div>

              <Button onClick={submit} disabled={submitting} className={`w-full h-14 text-lg bg-${colorMain}-600 hover:bg-${colorMain}-700 disabled:opacity-50`} data-testid="mov-submit">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                {submitting ? "Salvataggio..." : `Conferma ${isIn ? "Carico" : "Scarico"}`}
              </Button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-rose-100 p-5 shadow-sm">
          <h4 className="font-semibold text-[#5A3A3A] mb-3 text-sm">Ultimi movimenti</h4>
          {recent.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Nessun movimento recente</p>
          ) : (
            <div className="space-y-2">
              {recent.map((m) => (
                <div key={m.movement_id} className="border-b border-rose-50 pb-2">
                  <p className="text-sm font-medium truncate">{m.product_name || "?"}</p>
                  <p className="text-xs text-gray-500 flex justify-between">
                    <span>{new Date(m.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                    <span className={`font-bold ${isIn ? "text-emerald-600" : "text-rose-600"}`}>{isIn ? "+" : "−"}{m.quantity}</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===================== QUICK CREATE PRODUCT (in fase di carico) =====================
function QuickCreateProduct({ ean, onCancel, onCreated }) {
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [line, setLine] = useState("retail");
  const [price, setPrice] = useState(0);
  const [minStock, setMinStock] = useState(10);
  const [busy, setBusy] = useState(false);

  // SKU suggerito di default partendo dall'EAN/codice scansionato
  useEffect(() => {
    if (ean) setSku(`NEW-${(ean || "").toString().slice(-6).toUpperCase()}`);
  }, [ean]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Nome prodotto obbligatorio");
    if (!sku.trim()) return toast.error("SKU obbligatorio");
    if (busy) return;
    setBusy(true);
    const { ok, data } = await wfJson("/warehouse/products", {
      method: "POST",
      body: JSON.stringify({
        sku: sku.trim(),
        ean: (ean || "").trim(),
        name: name.trim(),
        line,
        category: "generico",
        price: parseFloat(price) || 0,
        stock: 0,
        min_stock: parseInt(minStock) || 0,
      }),
    });
    setBusy(false);
    if (ok) onCreated(data);
    else toast.error(data.detail || "Errore creazione prodotto");
  };

  return (
    <div className="border-2 border-emerald-300 bg-emerald-50/50 rounded-2xl p-5 space-y-3" data-testid="quick-create-product">
      <div className="flex items-start gap-3">
        <div className="bg-emerald-100 p-2 rounded-xl">
          <Plus className="w-5 h-5 text-emerald-700" />
        </div>
        <div className="flex-1">
          <p className="font-bold text-emerald-900">Codice non in anagrafica</p>
          <p className="text-sm text-emerald-700">
            Stai caricando un prodotto <b>nuovo</b>. Compila i dati base e crealo al volo: poi indicherai la quantità.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs uppercase font-semibold text-gray-600">Nome prodotto *</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-12 mt-1" placeholder="es. Ioxi Hair Shampoo PRO 500ml" data-testid="qcp-name" />
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-600">SKU interno *</label>
          <Input value={sku} onChange={(e) => setSku(e.target.value.toUpperCase())} className="h-12 mt-1 font-mono" placeholder="es. IOX-P-SHA500" data-testid="qcp-sku" />
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-600">Codice EAN (dal barcode)</label>
          <Input value={ean} disabled className="h-12 mt-1 font-mono bg-gray-50" />
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-600">Linea</label>
          <select value={line} onChange={(e) => setLine(e.target.value)} className="h-12 mt-1 w-full border rounded-lg px-3 text-base" data-testid="qcp-line">
            <option value="retail">Retail</option>
            <option value="professional">Professional</option>
            <option value="espositore">Espositore</option>
            <option value="marketing">Marketing</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-600">Soglia minima</label>
          <Input type="number" value={minStock} onChange={(e) => setMinStock(e.target.value)} className="h-12 mt-1" data-testid="qcp-minstock" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase font-semibold text-gray-600">Prezzo unitario (€, opzionale)</label>
          <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="h-12 mt-1" data-testid="qcp-price" />
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="flex-1 h-12">Annulla</Button>
        <Button onClick={submit} disabled={busy} className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-base" data-testid="qcp-submit">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          {busy ? "Creazione..." : "Crea prodotto e procedi"}
        </Button>
      </div>
    </div>
  );
}

// ===================== ORDINI =====================
function OrdersView() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("pending");
  const [showNew, setShowNew] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    const q = tab === "all" ? "" : `?status=${tab}`;
    wfJson(`/warehouse/orders${q}`).then(({ data }) => setOrders(data.items || []));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  if (active) return <OrderDetail order={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-[#5A3A3A] flex items-center gap-2">
          <Package className="w-7 h-7" /> Evasione ordini
        </h2>
        <Button onClick={() => setShowNew(true)} className="h-12 px-5 bg-sky-600 hover:bg-sky-700" data-testid="new-order-btn">
          <Plus className="w-5 h-5 mr-1" /> Nuovo ordine
        </Button>
      </div>

      <div className="flex gap-2 border-b border-rose-100" data-testid="orders-tabs">
        {[
          { id: "pending", label: "Da preparare" },
          { id: "preparing", label: "In preparazione" },
          { id: "shipped", label: "Spediti" },
          { id: "all", label: "Tutti" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              tab === t.id ? "border-sky-500 text-sky-700" : "border-transparent text-gray-500 hover:text-[#5A3A3A]"
            }`}
            data-testid={`orders-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {orders.map((o) => (
          <button
            key={o.order_id}
            onClick={() => setActive(o)}
            className="bg-white border border-rose-100 hover:border-sky-300 rounded-2xl p-4 text-left hover:shadow-md transition"
            data-testid={`order-card-${o.order_number}`}
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="font-bold text-[#5A3A3A]">{o.order_number}</p>
                <p className="text-sm text-gray-600">{o.customer_name}</p>
                <p className="text-xs text-gray-400">{o.customer_city} · {o.items?.length || 0} righe</p>
              </div>
              <StatusBadge status={o.status} />
            </div>
            {o.ddt_number && (
              <p className="text-xs text-amber-700 mt-2 font-mono">📄 {o.ddt_number} · {o.corriere}</p>
            )}
          </button>
        ))}
        {orders.length === 0 && <p className="col-span-2 text-center text-gray-400 py-10">Nessun ordine</p>}
      </div>

      {showNew && <NewOrderModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Da preparare", cls: "bg-amber-100 text-amber-800" },
    preparing: { label: "In preparazione", cls: "bg-sky-100 text-sky-800" },
    shipped: { label: "Spedito", cls: "bg-emerald-100 text-emerald-800" },
    cancelled: { label: "Annullato", cls: "bg-gray-100 text-gray-600" },
  };
  const c = map[status] || map.pending;
  return <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${c.cls}`}>{c.label}</span>;
}

function NewOrderModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_name: "", customer_address: "", customer_city: "", customer_cap: "",
    customer_province: "", customer_phone: "", customer_email: "", notes: "",
  });
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]); // {product_id, sku, name, quantity, price}
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    wfJson("/warehouse/products").then(({ data }) => setProducts(data.items || []));
  }, []);

  const addItem = (p) => {
    const exists = items.find((it) => it.product_id === p.product_id);
    if (exists) {
      setItems(items.map((it) => it.product_id === p.product_id ? { ...it, quantity: it.quantity + 1 } : it));
    } else {
      setItems([...items, { product_id: p.product_id, sku: p.sku, name: p.name, quantity: 1, price: p.price || 0 }]);
    }
    setPickerOpen(false);
  };

  const updateQty = (idx, v) => {
    const n = Math.max(1, parseInt(v) || 1);
    setItems(items.map((it, i) => i === idx ? { ...it, quantity: n } : it));
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.customer_name) return toast.error("Nome cliente obbligatorio");
    if (items.length === 0) return toast.error("Aggiungi almeno un prodotto");
    const { ok, data } = await wfJson("/warehouse/orders", { method: "POST", body: JSON.stringify({ ...form, items }) });
    if (ok) { toast.success("Ordine creato"); onCreated(); }
    else toast.error(data.detail || "Errore");
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="new-order-modal">
        <div className="sticky top-0 bg-white border-b border-rose-100 p-5 flex justify-between items-center">
          <h3 className="text-xl font-bold text-[#5A3A3A]">Nuovo ordine</h3>
          <button onClick={onClose}><X className="w-6 h-6" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <h4 className="font-semibold text-sm text-gray-500 uppercase mb-2">Destinatario</h4>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Nome / Ragione sociale *" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="col-span-2 h-12" data-testid="order-customer-name" />
              <Input placeholder="Indirizzo" value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="col-span-2 h-11" />
              <Input placeholder="CAP" value={form.customer_cap} onChange={(e) => setForm({ ...form, customer_cap: e.target.value })} className="h-11" />
              <Input placeholder="Città" value={form.customer_city} onChange={(e) => setForm({ ...form, customer_city: e.target.value })} className="h-11" />
              <Input placeholder="Provincia (es. TO)" value={form.customer_province} onChange={(e) => setForm({ ...form, customer_province: e.target.value })} className="h-11" />
              <Input placeholder="Telefono" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="h-11" />
              <Input placeholder="Email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="col-span-2 h-11" />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold text-sm text-gray-500 uppercase">Prodotti ({items.length})</h4>
              <Button onClick={() => setPickerOpen(true)} className="bg-sky-600 hover:bg-sky-700 h-9" data-testid="order-add-product">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
            </div>
            <div className="border rounded-xl divide-y">
              {items.length === 0 ? (
                <p className="text-center text-gray-400 py-5 text-sm">Nessun prodotto aggiunto</p>
              ) : (
                items.map((it, idx) => (
                  <div key={idx} className="p-3 flex items-center gap-2" data-testid={`order-line-${idx}`}>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{it.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{it.sku} · €{(it.price || 0).toFixed(2)}/pz</p>
                    </div>
                    <Input type="number" min="1" value={it.quantity} onChange={(e) => updateQty(idx, e.target.value)} className="h-10 w-20 text-center font-bold" />
                    <button onClick={() => removeItem(idx)} className="text-rose-500 p-2 hover:bg-rose-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))
              )}
            </div>
          </div>

          <Input placeholder="Note interne (opzionale)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-11" />

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 h-12">Annulla</Button>
            <Button onClick={submit} className="flex-1 h-12 bg-sky-600 hover:bg-sky-700 text-base" data-testid="order-save">Crea ordine</Button>
          </div>
        </div>

        {pickerOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setPickerOpen(false)}>
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[70vh] overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
              <h4 className="font-bold mb-3">Seleziona prodotto</h4>
              <div className="space-y-1">
                {products.map((p) => (
                  <button key={p.product_id} onClick={() => addItem(p)} className="w-full text-left p-2.5 hover:bg-rose-50 rounded-lg border-b" data-testid={`picker-product-${p.sku}`}>
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-xs text-gray-500 font-mono">{p.sku} · stock {p.stock} · €{(p.price || 0).toFixed(2)}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderDetail({ order: initialOrder, onBack }) {
  const [order, setOrder] = useState(initialOrder);
  const [shipping, setShipping] = useState({ corriere: "GLS", tracking: "" });
  const [shippingBusy, setShippingBusy] = useState(false);

  const reload = useCallback(() => {
    wfJson(`/warehouse/orders/${order.order_id}`).then(({ data }) => setOrder(data));
  }, [order.order_id]);

  const toggleItem = async (idx) => {
    const newItems = order.items.map((it, i) => i === idx ? { ...it, prepared: !it.prepared } : it);
    const { ok, data } = await wfJson(`/warehouse/orders/${order.order_id}/prepare`, {
      method: "PUT",
      body: JSON.stringify({ items: newItems }),
    });
    if (ok) setOrder(data);
  };

  const shipNow = async () => {
    if (shippingBusy) return;
    if (!order.items.every((it) => it.prepared)) {
      return toast.error("Spunta tutte le righe prima di spedire");
    }
    setShippingBusy(true);
    const { ok, data } = await wfJson(`/warehouse/orders/${order.order_id}/ship`, {
      method: "POST", body: JSON.stringify(shipping),
    });
    if (ok && data.success) {
      toast.success(`Ordine spedito! DDT: ${data.ddt_number}`);
      reload();
    } else {
      toast.error(data.detail || "Errore durante la spedizione");
    }
    setShippingBusy(false);
  };

  const downloadDdt = () => {
    window.open(`${API}/warehouse/orders/${order.order_id}/ddt-pdf`, "_blank");
  };

  const allPrepared = order.items?.every((it) => it.prepared);
  const shipped = order.status === "shipped";

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-[#5A3A3A]" data-testid="order-back">
        <ChevronLeft className="w-4 h-4" /> Indietro
      </button>

      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#5A3A3A]">{order.order_number}</h2>
          <p className="text-gray-600">{order.customer_name}</p>
          {order.customer_address && (
            <p className="text-sm text-gray-500">
              {order.customer_address}, {order.customer_cap} {order.customer_city} {order.customer_province && `(${order.customer_province})`}
            </p>
          )}
          {order.customer_phone && <p className="text-sm text-gray-500">📞 {order.customer_phone}</p>}
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="bg-white rounded-2xl border border-rose-100 p-5 space-y-2">
        <h4 className="font-semibold text-[#5A3A3A] mb-2">
          Righe da preparare ({order.items?.filter((i) => i.prepared).length || 0}/{order.items?.length || 0})
        </h4>
        {order.items?.map((it, idx) => (
          <button
            key={idx}
            onClick={() => !shipped && toggleItem(idx)}
            disabled={shipped}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition text-left ${
              it.prepared ? "bg-emerald-50 border-emerald-300" : "bg-white border-gray-200 hover:border-sky-300"
            } ${shipped ? "opacity-60 cursor-default" : ""}`}
            data-testid={`order-item-${idx}`}
          >
            {it.prepared ? <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0" /> : <Circle className="w-7 h-7 text-gray-300 shrink-0" />}
            <div className="flex-1">
              <p className="font-medium">{it.name}</p>
              <p className="text-xs text-gray-500 font-mono">{it.sku}</p>
            </div>
            <p className="text-2xl font-bold text-[#5A3A3A]">{it.quantity}</p>
          </button>
        ))}
      </div>

      {!shipped && (
        <div className={`bg-white rounded-2xl border-2 p-5 space-y-3 ${allPrepared ? "border-emerald-300" : "border-gray-200"}`}>
          <h4 className="font-semibold text-[#5A3A3A]">Spedizione</h4>
          <div className="grid grid-cols-2 gap-3">
            <select
              value={shipping.corriere}
              onChange={(e) => setShipping({ ...shipping, corriere: e.target.value })}
              className="h-12 border rounded-xl px-3 font-medium"
              data-testid="ship-corriere"
            >
              <option value="GLS">GLS</option>
              <option value="BRT">BRT - Bartolini</option>
              <option value="SDA">SDA</option>
              <option value="DHL">DHL</option>
              <option value="UPS">UPS</option>
              <option value="Poste">Poste Italiane</option>
              <option value="Altro">Altro</option>
            </select>
            <Input
              value={shipping.tracking}
              onChange={(e) => setShipping({ ...shipping, tracking: e.target.value })}
              placeholder="Tracking (opzionale)"
              className="h-12"
              data-testid="ship-tracking"
            />
          </div>
          <Button
            onClick={shipNow}
            disabled={!allPrepared || shippingBusy}
            className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-base disabled:opacity-50"
            data-testid="ship-now-btn"
          >
            <Truck className="w-5 h-5 mr-2" />
            {shippingBusy ? "Spedizione in corso..." : (allPrepared ? "Spedisci e genera DDT" : "Spunta tutte le righe per spedire")}
          </Button>
        </div>
      )}

      {shipped && order.ddt_number && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs text-amber-700 uppercase font-semibold">Documento di trasporto</p>
            <p className="text-2xl font-bold text-amber-900">{order.ddt_number}</p>
            <p className="text-sm text-amber-700">Corriere: {order.corriere} {order.tracking && `· Track: ${order.tracking}`}</p>
          </div>
          <Button onClick={downloadDdt} className="h-12 px-5 bg-amber-600 hover:bg-amber-700" data-testid="ddt-download">
            <FileText className="w-5 h-5 mr-2" /> Apri PDF DDT
          </Button>
        </div>
      )}
    </div>
  );
}

// ===================== DDT LIST =====================
function DdtView() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    wfJson("/warehouse/ddt").then(({ data }) => setItems(data.items || []));
  }, []);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-[#5A3A3A] flex items-center gap-2">
        <Truck className="w-7 h-7" /> Documenti di trasporto (DDT)
      </h2>
      <p className="text-gray-500 text-sm">Storico DDT generati dall'evasione ordini.</p>

      <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#fdf6f3]">
            <tr className="text-left text-xs uppercase text-[#5A3A3A]">
              <th className="px-4 py-3">N. DDT</th>
              <th className="px-4 py-3">Ordine</th>
              <th className="px-4 py-3">Destinatario</th>
              <th className="px-4 py-3">Corriere</th>
              <th className="px-4 py-3">Tracking</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rose-50">
            {items.map((d) => (
              <tr key={d.ddt_id} data-testid={`ddt-row-${d.ddt_number}`}>
                <td className="px-4 py-3 font-bold text-amber-700">{d.ddt_number}</td>
                <td className="px-4 py-3 text-sm font-mono">{d.order_number}</td>
                <td className="px-4 py-3 text-sm">{d.customer_name}</td>
                <td className="px-4 py-3 text-sm">{d.corriere}</td>
                <td className="px-4 py-3 text-xs font-mono text-gray-500">{d.tracking || "—"}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{new Date(d.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => window.open(`${API}/warehouse/orders/${d.order_id}/ddt-pdf`, "_blank")}
                    className="text-amber-700 hover:text-amber-900 flex items-center gap-1 text-sm"
                    data-testid={`ddt-open-${d.ddt_number}`}
                  >
                    <Download className="w-4 h-4" /> PDF
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-gray-400 py-8">Nessun DDT generato</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== CAMERA SCANNER =====================
function CameraScanner({ onCode, onClose }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [status, setStatus] = useState("Avvio fotocamera...");

  // ferma stream + reader (cleanup centralizzato)
  const cleanup = () => {
    try { readerRef.current?.reset?.(); } catch { /* ignore */ }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const handleResult = (text) => {
    cleanup();
    onCode(text);
  };

  // Start scanning con un constraint specifico (facingMode oppure deviceId)
  const startScan = useCallback(async (constraints) => {
    if (!videoRef.current) return;
    try {
      setStatus("Apertura fotocamera...");
      // 1) chiedo i permessi e ottengo lo stream
      const stream = await navigator.mediaDevices.getUserMedia({ video: constraints, audio: false });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      try { await videoRef.current.play(); } catch { /* iOS richiede playsInline */ }

      // 2) ora che ho il permesso, posso enumerare i device per il selettore
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const cams = all.filter((d) => d.kind === "videoinput");
        setDevices(cams);
        const activeId = stream.getVideoTracks()[0]?.getSettings()?.deviceId || "";
        if (activeId) setDeviceId(activeId);
      } catch { /* ignore */ }

      // 3) avvio decoder ZXing sul video element
      if (!readerRef.current) readerRef.current = new BrowserMultiFormatReader();
      setStatus("Inquadra il codice a barre");
      readerRef.current.decodeFromVideoElement(videoRef.current, (result) => {
        if (result) handleResult(result.getText());
      });
    } catch (e) {
      if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
        setError("Permesso fotocamera negato. Vai in Impostazioni → Safari → Fotocamera → Consenti, poi ricarica.");
      } else if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError") {
        setError("Nessuna fotocamera disponibile su questo dispositivo.");
      } else if (e?.name === "NotReadableError") {
        setError("Fotocamera occupata da un'altra app. Chiudi le altre app e riprova.");
      } else if (!window.isSecureContext) {
        setError("La fotocamera richiede HTTPS. Apri il sito in modalità sicura.");
      } else {
        setError("Errore fotocamera: " + (e?.message || e?.name || "ignoto"));
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // su mobile preferisco la fotocamera posteriore via facingMode
    startScan({ facingMode: { ideal: "environment" } });
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchCamera = async (newId) => {
    setDeviceId(newId);
    cleanup();
    await startScan({ deviceId: { exact: newId } });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="camera-scanner-modal">
        <div className="bg-[#5A3A3A] text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            <h3 className="font-semibold">Scansiona codice a barre</h3>
          </div>
          <button onClick={() => { cleanup(); onClose(); }} className="hover:bg-white/10 p-1 rounded" data-testid="camera-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="relative bg-black aspect-[4/3]">
          <video ref={videoRef} className="w-full h-full object-cover" autoPlay playsInline muted />
          {!error && (
            <>
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="border-2 border-rose-400 rounded-2xl w-3/4 h-1/3 shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
              </div>
              <div className="absolute bottom-3 left-0 right-0 text-center text-white text-xs bg-black/40 py-1.5">
                {status}
              </div>
            </>
          )}
          {error && (
            <div className="absolute inset-0 bg-rose-900/85 flex items-center justify-center text-white p-6 text-center text-sm">
              {error}
            </div>
          )}
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-gray-500 text-center">Inquadra il codice EAN/QR. Il riconoscimento è automatico.</p>
          {devices.length > 1 && (
            <select
              value={deviceId}
              onChange={(e) => switchCamera(e.target.value)}
              className="w-full h-10 border rounded-lg px-3 text-sm"
              data-testid="camera-device-select"
            >
              {devices.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
              ))}
            </select>
          )}
        </div>
      </div>
    </div>
  );
}

// ===================== SETTINGS =====================
function SettingsView() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const change = async () => {
    if (next.length < 4) return toast.error("Il nuovo PIN deve avere almeno 4 cifre");
    if (next !== confirm) return toast.error("I due PIN non corrispondono");
    if (!/^\d+$/.test(next)) return toast.error("Solo numeri");
    const { ok, data } = await wfJson("/warehouse/pin/update", {
      method: "PUT", body: JSON.stringify({ current_pin: current, new_pin: next }),
    });
    if (ok) {
      toast.success("PIN aggiornato!");
      setCurrent(""); setNext(""); setConfirm("");
    } else {
      toast.error(data.detail || "Errore");
    }
  };

  return (
    <div className="space-y-5 max-w-md">
      <h2 className="text-2xl font-bold text-[#5A3A3A] flex items-center gap-2">
        <Settings className="w-7 h-7" /> Impostazioni
      </h2>

      <div className="bg-white rounded-2xl border border-rose-100 p-5 space-y-4">
        <div className="flex items-center gap-2 text-[#5A3A3A]">
          <KeyRound className="w-5 h-5" />
          <h3 className="font-semibold">Cambia PIN di accesso</h3>
        </div>
        <Input type="password" inputMode="numeric" placeholder="PIN attuale" value={current} onChange={(e) => setCurrent(e.target.value)} className="h-12" data-testid="pin-current" />
        <Input type="password" inputMode="numeric" placeholder="Nuovo PIN (min 4 cifre)" value={next} onChange={(e) => setNext(e.target.value)} className="h-12" data-testid="pin-new" />
        <Input type="password" inputMode="numeric" placeholder="Conferma nuovo PIN" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12" data-testid="pin-confirm-new" />
        <Button onClick={change} className="w-full h-12 bg-[#B87A8A] hover:bg-[#a06978]" data-testid="pin-change-submit">
          Aggiorna PIN
        </Button>
      </div>
    </div>
  );
}
